import { useEffect } from 'react';

function controlTarget(node) {
  if (!(node instanceof Element)) return null;
  if (node.closest('.home-intro')) return null;
  const control = node.closest('a[href], button, [role="button"], [role="link"]');
  if (!control || control.matches(':disabled, [aria-disabled="true"]')) return null;
  return control;
}

function selectionTarget(node) {
  if (!(node instanceof Element)) return null;
  const book = node.closest('.home-gold-surface .bible-list a');
  if (book) return book;
  if (!node.closest('.bibleframe .content section')) return null;
  const block = node.closest('.bible-reading-verse, .gal-verse, .hebrew-verse');
  if (block) return block;
  // Older editions contain nested verse spans and a separate verse number.
  let verse = node.closest('span[id="verse"]');
  if (!verse) {
    const sibling = node.closest('em[id]')?.nextElementSibling;
    if (sibling?.matches('span[id="verse"]')) verse = sibling;
  }
  if (verse) {
    while (verse.parentElement?.closest('span[id="verse"]')) {
      verse = verse.parentElement.closest('span[id="verse"]');
    }
    return verse;
  }
  return node.closest('p, h1, h2, h3, h4, h5, h6, li, a');
}

export default function BibleSelectionSound() {
  useEffect(() => {
    let context;
    let lastHoverTarget;
    let lastHoverTime = -Infinity;
    let lastClickSound = -Infinity;
    const unlock = () => {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        if (!context) context = new AudioContext();
        if (context.state === 'suspended') return context.resume().catch(() => {});
      } catch (_) { /* Sound is optional; reading must remain available. */ }
    };
    const play = (isControl, isClick = false) => {
      if (!context || context.state !== 'running') return;
      const now = context.currentTime;
      if (isClick && now - lastClickSound < 0.09) return;
      if (isClick) lastClickSound = now;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = isClick ? 'triangle' : isControl ? 'square' : 'sine';
      if (isClick) {
        oscillator.frequency.setValueAtTime(740, now);
        oscillator.frequency.exponentialRampToValueAtTime(280, now + 0.085);
      } else if (isControl) {
        oscillator.frequency.setValueAtTime(880, now);
        oscillator.frequency.setValueAtTime(660, now + 0.03);
      } else {
        oscillator.frequency.setValueAtTime(1046.5, now);
        oscillator.frequency.exponentialRampToValueAtTime(1568, now + 0.025);
      }
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(isClick ? 0.045 : isControl ? 0.018 : 0.035, now + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (isClick ? 0.115 : 0.065));
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
      oscillator.start(now);
      oscillator.stop(now + (isClick ? 0.12 : 0.07));
    };
    const hover = (event) => {
      if (event.pointerType === 'touch') return;
      const control = controlTarget(event.target);
      const target = control || selectionTarget(event.target);
      const previous = controlTarget(event.relatedTarget) || selectionTarget(event.relatedTarget);
      if (!target || target === previous) return;
      // Only deduplicate focus immediately following a hover on the same control.
      // A new pointer entry must sound even when moving quickly between links.
      if (event.type === 'focusin' && target === lastHoverTarget &&
          context && context.currentTime - lastHoverTime < 0.09) return;
      if (context?.state === 'running') {
        lastHoverTarget = target;
        lastHoverTime = context.currentTime;
      }
      play(Boolean(control));
    };
    const keydown = (event) => {
      if (!event.repeat && !event.metaKey && !event.ctrlKey && !event.altKey) unlock();
    };
    const click = (event) => {
      if (event.button !== 0 || !controlTarget(event.target)) return;
      const resuming = unlock();
      if (resuming) resuming.then(() => play(true, true));
      else play(true, true);
    };
    document.addEventListener('pointerdown', unlock);
    document.addEventListener('keydown', keydown);
    document.addEventListener('pointerover', hover);
    document.addEventListener('focusin', hover);
    document.addEventListener('click', click, true);
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('pointerover', hover);
      document.removeEventListener('focusin', hover);
      document.removeEventListener('click', click, true);
      if (context && context.state !== 'closed') context.close().catch(() => {});
    };
  }, []);
  return null;
}
